import Button from "devextreme-react/button";
import { useContext } from "react";
import { showErrorMessage } from "../../../../utils/messages";
import { MaterialOrderContext } from "../context/MaterialOrderContext";
import { useNavigate, useSearchParams } from "react-router-dom";
import { RetailOrderContext } from "../../retail-order/context/RetailOrderContext";
import { useSubmitMaterialOrder } from "../api/submit-material-order";
import { MaterialOrderDetails } from "../types/MaterialOrderDetails";
import { AR, ProductLine, ROType, StockingOrderWorkflow } from "../../../../types/enums";
import { RetailOrderDetails } from "../../retail-order/types/RetailOrderDetails";
import { roundMoney } from "../../../../utils/number-utils";
import { alert, confirm } from "devextreme/ui/dialog";
import { MOParentRetailOrder } from "../types/MOParentRetailOrder";
import { apiGetParentRetailOrder } from "../api/get-parent-retail-order";
import { LoadPagePanel } from "../../../../components";


export default function SubmitMaterialOrderButton() {

	const { order: materialOrder } = useContext(MaterialOrderContext);
	const { order: retailOrder } = useContext(RetailOrderContext);
	const navigate = useNavigate();
	const [searchParams] = useSearchParams();
	const returnUrl = encodeURIComponent(searchParams.get("returnUrl") ?? "");

	const submitMaterialOrder = useSubmitMaterialOrder(
		materialOrder.details,
		(result) => {
			alert(`Material order ${result.moNumber} has been created`, 'Information')
				.then(() => {
					if (materialOrder.details.retailOrderID) {
						if (retailOrder.details && retailOrder.details.activeARs.includes(AR.CfiToEnterScheduledInstallDate)) {
							navigate(`/cfi/retail-order/${materialOrder.details.retailOrderID}/143` +
								(!!returnUrl ? '?returnUrl=' + returnUrl : ''));
						}
						else {
							navigate(`/cfi/retail-order/${materialOrder.details.retailOrderID}/130/${result.materialOrderID}` +
								(!!returnUrl ? '?returnUrl=' + returnUrl : ''));
						}
					}
					else {
						navigate(`/cfi/material-order/${result.materialOrderID}` +
							(!!returnUrl ? '?returnUrl=' + returnUrl : ''));
					}
				});
		},
		(error) => {
			showErrorMessage(error);
		}
	);

	return (
		<>
			<Button
				text="Submit Order"
				icon="Inscyth-Save"
				elementAttr={{ 'data-testid': 'btn-submit-order' }}
				disabled={submitMaterialOrder.isLoading || !materialOrder.details.products || materialOrder.details.products.length === 0}
				validationGroup='newMaterialOrderValidationGroup'
				onClick={(e) => {
					validateForm(e.validationGroup)
						.then(() => validateMaterialOrder(materialOrder.details))
						.then(() => validateDates(materialOrder.details))
						.then(() => getParentRetailOrder(materialOrder.details.retailOrderID, retailOrder.details))
						.then((parentRetailOrder) => validate88(materialOrder.details, parentRetailOrder))
						.then((validationResult) => {
							if (!validationResult) {
								materialOrder.details.finLink = false;
							}
							submitMaterialOrder.mutate();
						})
						.catch((error) => { if (error) showErrorMessage(error); });
				}}
			>
			</Button>
			<LoadPagePanel visible={submitMaterialOrder.isLoading} />
		</>
	)
}

function validateForm(validationGroup: any): Promise<void> {
	return new Promise((resolve, reject) => {
		let validation = validationGroup.validate();
		return validation.isValid ? resolve() : reject();
	});
}

function validateMaterialOrder(mo: MaterialOrderDetails): Promise<void> {
	return new Promise((resolve, reject) => {
		const errors: string[] = [];

		if (!mo.products || mo.products.length === 0) {
			errors.push('Products must be added');
		}
		else {
			for (let product of mo.products) {

				if (product.quantity <= 0) {
					errors.push('Quantity must be greater than 0');
					break;
				}

				if (!product.productRequestedDeliveryDate) {
					errors.push('Requested Delivery Date is required');
					break;
				}

				if (product.productLineID === ProductLine.Granite && product.squareFootage && product.productType !== 'Sealer' &&
					product.quantity * parseFloat(product.squareFootage) < 45) {
					errors.push('Granite orders must be ordered in sq. ft. quantity, and the quantity of granite orders must meet or exceed a minimum of 45 sq. ft.');
					break;
				}
			}
		}

		if (errors.length > 0) {
			return reject(errors.join("<br/>"));
		}

		return resolve();
	});
}

function validateDates(mo: MaterialOrderDetails): Promise<void> {
	return new Promise((resolve, reject) => {

		let map: { [distributorName: string]: string; } = {};
		let distributors: string[] = [];
		for (let product of mo.products) {
			if (product.distributorName) {
				if (map[product.distributorName] &&
					map[product.distributorName] !== new Date(product.productRequestedDeliveryDate!).toDateString()) {
					if (!distributors.includes(product.distributorName)) {
						distributors.push(product.distributorName);
					}
				}
				else {
					map[product.distributorName] = new Date(product.productRequestedDeliveryDate!).toDateString()
				}
			}
		}

		if (distributors.length > 0) {
			return confirm(`You have requested different requested delivery dates of products from the same distributor (${distributors.join(", ")}). <br/> This could lead to additional freight charges. Are you sure you wish to submit the order?`, 'Warning')
				.then((confirmed) => {
					return confirmed ? resolve() : reject();
				})
		}

		return resolve();
	});
}

function validate88(mo: MaterialOrderDetails, ro: MOParentRetailOrder | undefined): Promise<boolean> {
	return new Promise((resolve, reject) => {

		if (!ro || !mo.finLink) {
			return resolve(true);
		}

		if (ro.roTypeID === ROType.Display || ro.roTypeID === ROType.DisplayAdmin ||
			ro.roTypeID === ROType.Warranty || ro.roTypeID === ROType.CFICompensation) {
			return resolve(true);
		}

		const moTotal = mo.amount + ro.moTotal;

		let overBy = moTotal - roundMoney(ro.amount * 0.88);
		overBy = overBy > 0 ? overBy : 0;

		if (overBy === 0)
			return resolve(true);
		else {
			if (mo.stockingOrderWorkflowID === StockingOrderWorkflow.Blocked) {
				return reject("The materials you are ordering exceed the allotted 88% of the RO it is linked to.");
			}
			else {
				return confirm("The materials you are ordering exceed the allotted 88% of the RO it is linked to.<br/>The amount of the material ordered will be deducted from you.<br/>The MO number will be listed as a line item deduction on your upcoming payment stub.<br/>Do you wish to proceed?", "Warning")
					.then((confirmed) => {
						return confirmed ?
							resolve(false) : reject();
					});
			}
		}
	});
}

function getParentRetailOrder(retailOrderID: number | undefined, detailsFromContext: RetailOrderDetails): Promise<MOParentRetailOrder> | MOParentRetailOrder | undefined {

	if (detailsFromContext) {

		const result = {
			retailOrderID: detailsFromContext.retailOrderID,
			roTypeID: detailsFromContext.roTypeID,
			amount: detailsFromContext.amount,
			moTotal: 0
		} as MOParentRetailOrder;

		for (let neighbourMO of detailsFromContext.materialOrders) {
			if (neighbourMO.finLink) {
				result.moTotal += neighbourMO.amount;
			}
		}

		return result;
	}

	if (!retailOrderID)
		return undefined;

	return apiGetParentRetailOrder(retailOrderID!);
}
